/**
 * Attachment Upload Section Component
 * ===================================
 * 
 * Wrapper component that manages file attachment upload and display for form fields.
 * This component provides the interface between individual form components and
 * the attachment functionality, handling both file uploads and attachment viewing.
 * 
 * Purpose:
 * - Renders attachment upload interface for file requirements
 * - Displays uploaded attachments in a grid viewer
 * - Manages attachment lifecycle (upload, view, manage)
 * - Integrates with AttachmentUploadService for data persistence
 * 
 * Usage:
 * - Rendered in any form that has file attachment requirements
 * - Receives attachment requirements from parent form components
 * - Automatically handles attachment state changes
 * - Provides consistent attachment UI across all forms
 * 
 * Component Architecture:
 * - Wrapper component that renders AttachmentUploader and AttachmentGridViewer
 * - Manages attachment display and interaction logic
 * - Integrates with form validation and submission workflows
 * - Responsive layout with side-by-side upload and viewing
 * 
 * Layout Features:
 * - Two-column responsive layout (uploader + viewer)
 * - Viewer only shows when attachments exist
 * - Mobile-first design with stacked columns on small screens
 * - Consistent styling with form design patterns
 * 
 * @author MacSafety Development Team
 * @version 1.0
 */

import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { AttachmentUploadService } from '../../../../services/attachment-upload.service';
import { AttachmentUploadInfo } from '../../../../models/forms/attachments/form-attachment-requirements';

@Component({
  selector: 'app-attachment-upload-section',
  template: `
    <!-- 
      ATTACHMENT FIELD LABEL
      =======================
      Displays the attachment field label from form requirements.
      Label is dynamically set based on uploadInfo input.
      Uses floating label style for consistent form appearance.
    -->
    <ion-label position="floating" class="form-label">
      <strong>{{ uploadInfo.label }}:</strong>
    </ion-label>

    <!-- 
      ATTACHMENT ROW LAYOUT
      ======================
      Responsive row layout for attachment components.
      Uses 12 columns on mobile, 6 columns on large screens.
      Provides side-by-side upload and viewing experience.
    -->
    <ion-row class="attachment-row">
      
      <!-- 
        ATTACHMENT UPLOADER COLUMN
        ===========================
        Left column containing the file upload interface.
        Always visible to allow new file uploads.
        Responsive sizing for optimal mobile experience.
      -->
      <ion-col size="12" size-lg="6" class="attachment-uploader-col">
        <app-attachment-uploader
          [attachmentUploadInfo]="uploadInfo">
        </app-attachment-uploader>
      </ion-col>

      <!-- 
        ATTACHMENT VIEWER COLUMN
        =========================
        Right column containing the attachment display interface.
        Only shows when attachments exist for the field.
        Responsive sizing with conditional rendering.
      -->
      <ion-col size="12" size-lg="6" class="attachment-viewer-col" *ngIf="attachments?.length">
        <app-attachment-grid-viewer
          [attachments]="attachments"
          [identifier]="uploadInfo.identifier">
        </app-attachment-grid-viewer>
      </ion-col>
    </ion-row>
  `,
  styles: [`
    /* 
      FORM CARD STYLING
      ==================
      Consistent styling for attachment form sections.
      Matches overall form design patterns and spacing.
    */
    .form-card {
      margin-bottom: 24px;
      border-radius: 8px;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }
  `]
})
export class AttachmentUploadSectionComponent {
  
  // ============================================================================
  // INPUT PROPERTIES
  // ============================================================================
  // These properties define the attachment requirements and configuration
  
  /** 
   * Attachment upload information defining the field requirements.
   * Contains identifier, label, file types, and validation rules.
   * Provided by parent form component based on form metadata.
   */
  @Input() uploadInfo!: AttachmentUploadInfo;

  constructor(private attachmentService: AttachmentUploadService) {}

  // ============================================================================
  // COMPUTED PROPERTIES
  // ============================================================================
  // These getters provide reactive access to attachment data
  
  /**
   * Gets the current attachments for the upload field.
   * Retrieves attachment data from the service based on field identifier.
   * Used to conditionally render the attachment viewer component.
   * 
   * @returns Array of attachments for the current field, or undefined if none exist
   */
  get attachments() {
    return this.attachmentService.getAttachments(this.uploadInfo.identifier);
  }
} 